/**
 * Race-standings order for the hot-seat timing pane.
 * Place is independent of Player N names (CompetitorId + 1).
 */

export type StandingInput = {
  id: number;
  /** 1-based lap this boat is on. Higher laps rank ahead of gate count. */
  lap: number;
  checkpointsDone: number;
  elapsedMs: number;
  /** Official finish boat-clock; null while this seat is still racing. */
  officialTimeMs: number | null;
  /** Shared-split gap; used as a tie-break among unfinished seats. */
  behindMs: number | null;
};

export type StandingRank = {
  place: number;
  leader: boolean;
};

function compareStandings(left: StandingInput, right: StandingInput): number {
  const leftFinished = left.officialTimeMs !== null;
  const rightFinished = right.officialTimeMs !== null;

  if (leftFinished !== rightFinished) {
    return leftFinished ? -1 : 1;
  }

  if (leftFinished && rightFinished) {
    const leftTime = left.officialTimeMs ?? 0;
    const rightTime = right.officialTimeMs ?? 0;

    if (leftTime !== rightTime) {
      return leftTime - rightTime;
    }

    return left.id - right.id;
  }

  if (left.lap !== right.lap) {
    return right.lap - left.lap;
  }

  if (left.checkpointsDone !== right.checkpointsDone) {
    return right.checkpointsDone - left.checkpointsDone;
  }

  if (left.behindMs !== null && right.behindMs !== null && left.behindMs !== right.behindMs) {
    return left.behindMs - right.behindMs;
  }

  return left.id - right.id;
}

function displayBehindMs<T extends StandingInput>(
  seat: T,
  leader: T | undefined,
  index: number,
): number | null {
  if (!leader) {
    return seat.behindMs;
  }

  if (index === 0) {
    return 0;
  }

  if (leader.officialTimeMs !== null && seat.officialTimeMs !== null) {
    return seat.officialTimeMs - leader.officialTimeMs;
  }

  if (leader.behindMs !== null && seat.behindMs !== null) {
    return seat.behindMs - leader.behindMs;
  }

  return null;
}

/**
 * Finished boats first (official boat-clock, then seat id), then unfinished by
 * higher lap, then more gates this lap, then shared-split gap, then seat id. Place 1 is the race leader.
 * Raw elapsed is never used — an unplayed seat at 0 ms must not lead.
 * Output `behindMs` is vs that leader: finish delta as soon as both boats have
 * official times (do not wait for the field), otherwise a recentered shared-split
 * for a boat still racing — including after someone else has finished. Null only
 * when that boat has no shared split to compare.
 */
export function rankStandings<T extends StandingInput>(
  seats: readonly T[],
): Array<T & StandingRank> {
  const sorted = [...seats].sort(compareStandings);
  const leader = sorted[0];

  return sorted.map((seat, index) => ({
    ...seat,
    behindMs: displayBehindMs(seat, leader, index),
    place: index + 1,
    leader: index === 0,
  }));
}
